/**
 * Map controller — MapLibre GL JS v5.
 *
 * Replaces the previous Leaflet implementation. Architectural notes:
 *
 *  1. Tile source is plain OSM raster, defined INLINE in the `style` object
 *     at construction time. No external style spec; no API keys.
 *
 *  2. Runtime layers (route, cameras, roadworks) are added INSIDE the
 *     `map.on('load')` callback per the MapLibre 2025 contract: sources
 *     can be inlined but new layers must wait for the style to be ready.
 *
 *  3. Coordinate order: this file internally uses [lng, lat] (MapLibre +
 *     GeoJSON convention). The public surface accepts our existing
 *     `LatLng = {lat, lng}` type and converts at the boundary.
 *
 *  4. View behaviour:
 *      - 'north'  → always flat north-up (bearing=0, pitch=0); cursor
 *                   locked to screen center. Arrow when moving, circle
 *                   when stationary.
 *      - '2d'     → flat heading-up; cursor in lower third (road ahead).
 *      - 'tilted' → pitch 60°, heading-up; cursor in lower third.
 *  5. Mode toggle: one `easeTo` (~450 ms) so the camera glides cleanly.
 */
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

import { config } from './config';
import { thinLatLng } from './geoSimplify';
import { haversineMeters, splitRouteAt, type RouteIndex } from './routing';
import type { AmenityKind } from './amenities';
import type { LatLng, Roadwork, ViewMode } from './types';

/** OSMF public tiles — light / demo use only; not for heavy nav traffic. */
const OSM_TILE_URLS = [
  'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
  'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
  'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
];
type LineFeature = {
  type: 'Feature';
  properties: Record<string, unknown>;
  geometry: {
    type: 'LineString';
    coordinates: Array<[number, number]>;
  };
};
type LineFeatureCollection = {
  type: 'FeatureCollection';
  features: LineFeature[];
};
type PointFeature = {
  type: 'Feature';
  properties: Record<string, unknown>;
  geometry: {
    type: 'Point';
    coordinates: [number, number];
  };
};
type PointFeatureCollection = {
  type: 'FeatureCollection';
  features: PointFeature[];
};

const TILTED_PITCH_DEG = 60;
const PAN_THRESHOLD_METERS = 15;
const VIEW_TOGGLE_MS = 450;
const TICK_EASE_MS = 280;
/** Live follow ease — longer than a typical GPS tick so moves blend. */
const NAV_FOLLOW_MS = 1_000;
const NAV_FOLLOW_MS_MIN = 700;
const NAV_FOLLOW_MS_MAX = 1_400;
/** Skip only tiny GPS jitter; larger moves always ease. */
const NAV_CAM_SKIP_M = 2;
const NAV_CAM_SKIP_BEARING_DEG = 1;
const MIN_BOUNDS_SPAN_DEG = 0.00045;
/** Above this GPS speed the north-view marker is an arrow; at/below → circle. */
const MOVING_SPEED_MPS = 0.5;
/** Cap MapLibre LineString vertex count so setData stays interactive. */
const MAP_ROUTE_MAX_POINTS = 3_500;

/**
 * Wrapper around `map.addSource` / `addLayer`. Queues the ops while the
 * style is still loading; replays them as soon as `load` fires. This is
 * the 2025-safe pattern in MapLibre — sources can be inlined but
 * runtime-added layers must wait for `load`.
 */
type Op = (m: maplibregl.Map) => void;

export class NavMap {
  readonly map: maplibregl.Map;

  // -- runtime state -------------------------------------------------------
  private userMarker: maplibregl.Marker | null = null;
  /** Which visual the current userMarker element is rendering. */
  private userMarkerKind: 'dot' | 'arrow' | null = null;
  private stopMarkers: maplibregl.Marker[] = [];

  private follow = true;
  /** Last pan/ease target. Used by 2D's threshold-panTo heuristic and the
   *  re-center FAB. */
  private lastPanCenter: LatLng | null = null;

  /** Latest GPS fix the marker (in 2D) or the camera-easeTo (in tilted)
   *  should snap to. Keeps a single source of truth across both modes. */
  private pendingUserPos: LatLng | null = null;
  private pendingUserPosAccuracy: number | null = null;

  /** The CSS-arrow overlay rotation, in degrees (0..360). We hold a
   *  mirror in JS so the camera's `bearing` and the overlay's
   *  rotation can stay in sync. */
  private tiltingHeadingDeg = 0;

  private viewMode: ViewMode = 'north';
  private powerSaver = false;

  /** Optional UI hook when auto-follow / auto-zoom is toggled. */
  private followListener: ((following: boolean) => void) | null = null;
  /** Optional UI hook when the map bearing changes (compass rose). */
  private bearingListener: ((bearingDeg: number) => void) | null = null;
  /** Optional hook when the camera settles (map pan / programmatic move). */
  private moveEndListener: ((center: LatLng) => void) | null = null;

  /** Next manoeuvre point for recenter / nav framing. */
  private navLookAt: LatLng | null = null;
  /** Live navigation session — centered follow framing in north view. */
  private liveNavigation = false;
  /** Last camera frame inputs — skip easeTo when nothing meaningful moved. */
  private lastFollowCamUser: LatLng | null = null;
  private lastFollowCamLookAt: LatLng | null = null;
  private lastFollowCamBearing = Number.NaN;
  /** True when GPS reports meaningful motion (north marker shape). */
  private userMoving = false;
  /** Optional index for faster route-progress splits. */
  private routeProgressIndex: RouteIndex | null = null;
  /** Active route polyline — for full-route overview. */
  private routeCoords: LatLng[] = [];
  /** Faint alternative polylines (overview / planned only). */
  private altRouteCoords: LatLng[][] = [];
  private altSelectListener: ((index: number) => void) | null = null;

  /** True once the `load` event has fired and our GeoJSON sources are
   *  attached. Until then, mutator methods queue into `pendingOps`. */
  private styleReady = false;
  private readonly pendingOps: Op[] = [];
  private readonly container: HTMLElement;
  private longPressListener: ((p: LatLng) => void) | null = null;
  private longPressTimer: number | null = null;
  private longPressStart: { x: number; y: number; pointerId: number } | null = null;
  private readonly onLongPressPointerDown: (e: PointerEvent) => void;
  private readonly onLongPressPointerMove: (e: PointerEvent) => void;
  private readonly onLongPressPointerUp: (e: PointerEvent) => void;
  private readonly onLongPressContextMenu: (e: Event) => void;

  // -----------------------------------------------------------------------
  constructor(container: HTMLElement) {
    this.container = container;
    this.onLongPressPointerDown = (e) => this.handleLongPressPointerDown(e);
    this.onLongPressPointerMove = (e) => this.handleLongPressPointerMove(e);
    this.onLongPressPointerUp = (e) => this.handleLongPressPointerUp(e);
    this.onLongPressContextMenu = (e) => this.preventLongPressMenu(e);
    this.map = new maplibregl.Map({
      container,
      style: {
        version: 8,
        sources: {
          'osm-raster': {
            type: 'raster',
            tiles: OSM_TILE_URLS,
            tileSize: 256,
            attribution: '© OpenStreetMap',
          },
        },
        layers: [
          {
            id: 'osm-raster',
            type: 'raster',
            source: 'osm-raster',
            minzoom: 0,
            maxzoom: 22,
          },
        ],
      },
      center: [config.defaultCenter.lng, config.defaultCenter.lat],
      zoom: config.defaultZoom,
      minZoom: 3,
      maxZoom: 19,
      attributionControl: false,
      pitchWithRotate: false,
      dragRotate: true,
      touchPitch: false,
    });

    // MapLibre AttributionControl would sit bottom-right and steal
    // clicks from our #recenter FAB. Our custom `.attribution` strip
    // already covers OSM credit — skip the built-in chip.
    // (NavigationControl stays bottom-left for +/-.)
    this.map.addControl(
      new maplibregl.NavigationControl({ showCompass: false, visualizePitch: false }),
      'bottom-left',
    );

    // Stop auto-follow on any *user* map gesture. Programmatic easeTo/
    // fitBounds fire movestart WITHOUT originalEvent — those must keep
    // follow engaged.
    this.map.on('movestart', (e) => {
      if (e.originalEvent) this.setFollow(false);
    });

    const syncCompassFromMap = (): void => {
      this.syncUserMarkerRotation();
      this.bearingListener?.(this.map.getBearing());
    };
    this.map.on('rotate', syncCompassFromMap);
    this.map.on('move', syncCompassFromMap);
    this.map.on('moveend', () => {
      const c = this.map.getCenter();
      this.moveEndListener?.({ lat: c.lat, lng: c.lng });
    });

    this.map.on('load', () => this.handleStyleLoad());
    document.body.dataset.mapFollow = '1';

    const canvas = this.map.getCanvas();
    canvas.addEventListener('pointerdown', this.onLongPressPointerDown);
    canvas.addEventListener('pointermove', this.onLongPressPointerMove);
    canvas.addEventListener('pointerup', this.onLongPressPointerUp);
    canvas.addEventListener('pointercancel', this.onLongPressPointerUp);
    canvas.addEventListener('contextmenu', this.onLongPressContextMenu);
  }

  // -----------------------------------------------------------------------
  // Boot — sources + layers come online once the style is ready.
  // -----------------------------------------------------------------------
  private handleStyleLoad(): void {
    this.styleReady = true;

    // Route line: ordered ABOVE raster, BELOW cameras/roadworks.
    // Alternatives sit under the primary route so the active path wins.
    this.map.addSource('route-alts', {
      type: 'geojson',
      data: emptyLineCollection(),
    });
    this.map.addLayer({
      id: 'route-alts-line',
      type: 'line',
      source: 'route-alts',
      layout: {
        'line-join': 'round',
        'line-cap': 'butt',
        visibility: 'visible',
      },
      paint: {
        'line-color': '#2563eb',
        'line-width': 5.5,
        'line-opacity': 0.9,
        'line-dasharray': [2.2, 1.6],
      },
    });
    this.map.addLayer({
      id: 'route-alts-hit',
      type: 'line',
      source: 'route-alts',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
        visibility: 'visible',
      },
      paint: {
        'line-color': '#2563eb',
        'line-width': 22,
        'line-opacity': 0,
      },
    });
    this.map.addSource('route-done', {
      type: 'geojson',
      data: emptyLineString(),
    });
    this.map.addLayer({
      id: 'route-done-line',
      type: 'line',
      source: 'route-done',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
        visibility: 'visible',
      },
      paint: {
        'line-color': '#64748b',
        'line-width': 4,
        'line-opacity': 0.35,
      },
    });
    this.map.addSource('route', {
      type: 'geojson',
      data: emptyLineString(),
    });
    this.map.addLayer({
      id: 'route-line',
      type: 'line',
      source: 'route',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
        visibility: 'visible',
      },
      paint: {
        'line-color': '#38bdf8',
        'line-width': 6,
        'line-opacity': 0.85,
      },
    });

    this.map.on('click', 'route-alts-hit', (e) => {
      const feature = e.features?.[0];
      if (!feature || !this.altSelectListener) return;
      const raw = feature.properties?.altIndex;
      const index = typeof raw === 'number' ? raw : Number(raw);
      if (!Number.isInteger(index) || index < 0) return;
      e.preventDefault();
      this.altSelectListener(index);
    });
    this.map.on('mouseenter', 'route-alts-hit', () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });
    this.map.on('mouseleave', 'route-alts-hit', () => {
      this.map.getCanvas().style.cursor = '';
    });

    // Speed cameras — orange circles. Live in their own GPU layer so we
    // can blast 50–200+ markers per refresh without touching the DOM.
    this.map.addSource('cameras', {
      type: 'geojson',
      data: emptyFeatureCollection(),
    });
    this.map.addLayer({
      id: 'cameras-layer',
      type: 'circle',
      source: 'cameras',
      paint: {
        'circle-radius': 7,
        'circle-color': '#f97316',
        'circle-stroke-color': '#0f172a',
        'circle-stroke-width': 1.5,
        'circle-opacity': 0.85,
      },
    });

    // Roadworks / closures — amber vs. red using a `match` expression on
    // the `kind` property of each feature.
    this.map.addSource('roadworks', {
      type: 'geojson',
      data: emptyFeatureCollection(),
    });
    this.map.addLayer({
      id: 'roadworks-layer',
      type: 'circle',
      source: 'roadworks',
      paint: {
        'circle-radius': 9,
        'circle-color': [
          'match',
          ['get', 'kind'],
          'closure',
          '#ef4444',
          /* default construction */ '#f59e0b',
        ],
        'circle-stroke-color': [
          'match',
          ['get', 'kind'],
          'closure',
          '#7f1d1d',
          '#7c2d12',
        ],
        'circle-stroke-width': 2,
        'circle-opacity': 0.95,
      },
    });

    this.map.addSource('amenities', {
      type: 'geojson',
      data: emptyFeatureCollection(),
    });
    this.map.addLayer({
      id: 'amenities-layer',
      type: 'circle',
      source: 'amenities',
      paint: {
        'circle-radius': 8,
        'circle-color': [
          'match',
          ['get', 'kind'],
          'fuel',
          '#22c55e',
          'toilet',
          '#a78bfa',
          'rest',
          '#38bdf8',
          '#94a3b8',
        ],
        'circle-stroke-color': '#0f172a',
        'circle-stroke-width': 1.5,
        'circle-opacity': 0.92,
      },
    });

    // Replay all queued ops (setUserLocation / setDestination / drawRoute
    // etc. that fired while the style was still loading).
    for (const op of this.pendingOps) {
      try {
        op(this.map);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[NavMap] queued op failed:', err);
      }
    }
    this.pendingOps.length = 0;
  }

  /** Returns true if the op could run; queues it otherwise. */
  private runWhenReady(op: Op): void {
    if (this.styleReady) {
      try {
        op(this.map);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[NavMap] op failed:', err);
      }
    } else {
      this.pendingOps.push(op);
    }
  }

  // -----------------------------------------------------------------------
  // User position — the heart of the navigation UX.
  // -----------------------------------------------------------------------
  /**
   * Called on every fresh GPS fix.
   *
   *  - In `2d`: ensures the user marker exists, refreshes its position
   *    and (optionally) bumps radius to reflect accuracy; then runs
   *    threshold-panTo.
   *  - In `tilted`: hides the user marker and easeTo's the camera so the
   *    world "follows" the user smoothly. `bearingCtrl` (optional) is
   *    the current EMA-smoothed heading, applied as camera bearing.
   */
  setUserLocation(
    p: LatLng,
    accuracy?: number,
    opts: { moveCamera?: boolean } = {},
  ): void {
    this.pendingUserPos = p;
    this.pendingUserPosAccuracy = accuracy ?? null;
    const moveCamera = opts.moveCamera !== false;

    // Marker is ALWAYS geographic (MapLibre Marker at lat/lng) so panning
    // the map never leaves a screen-fixed "you are here" floating over
    // the wrong street. Camera follow is independent of the marker.
    this.runWhenReady(() => this.applyUserMarker(p, accuracy));

    if (!moveCamera || !this.follow) {
      this.lastPanCenter = p;
      return;
    }

    if (this.viewMode === 'north' && !this.liveNavigation) {
      this.thresholdPanTo(p);
      return;
    }

    const pose = this.cameraPose();
    this.runWhenReady((m) =>
      m.easeTo({
        center: [p.lng, p.lat],
        bearing: pose.bearing,
        pitch: pose.pitch,
        padding: pose.padding,
        duration: this.animMs(TICK_EASE_MS),
      }),
    );
  }

  /** Enable / disable camera lock onto the user. */
  setFollow(next: boolean): void {
    this.follow = next;
    document.body.dataset.mapFollow = next ? '1' : '0';
    this.followListener?.(next);
    this.syncFixedNavCursor();
  }

  setLiveNavigation(on: boolean): void {
    if (this.liveNavigation === on) return;
    this.liveNavigation = on;
    if (!on) {
      this.navLookAt = null;
      this.lastFollowCamUser = null;
      this.lastFollowCamLookAt = null;
      this.lastFollowCamBearing = Number.NaN;
    }
    this.syncFixedNavCursor();
    const p = this.pendingUserPos;
    if (!p) return;
    this.runWhenReady(() =>
      this.applyUserMarker(p, this.pendingUserPosAccuracy ?? undefined),
    );
  }

  /** Drive north-view marker shape: moving → arrow, stationary → circle. */
  setUserSpeed(speedMps: number | null): void {
    const moving =
      speedMps !== null && Number.isFinite(speedMps) && speedMps > MOVING_SPEED_MPS;
    if (this.userMoving === moving) return;
    this.userMoving = moving;
    const p = this.pendingUserPos;
    if (!p) return;
    this.runWhenReady(() =>
      this.applyUserMarker(p, this.pendingUserPosAccuracy ?? undefined),
    );
  }

  onFollowChange(listener: ((following: boolean) => void) | null): void {
    this.followListener = listener;
  }

  onMapBearingChange(listener: ((bearingDeg: number) => void) | null): void {
    this.bearingListener = listener;
  }

  onMoveEnd(listener: ((center: LatLng) => void) | null): void {
    this.moveEndListener = listener;
  }

  /** Current camera centre, or null before the first layout. */
  getCenter(): LatLng | null {
    try {
      const c = this.map.getCenter();
      if (!Number.isFinite(c.lat) || !Number.isFinite(c.lng)) return null;
      return { lat: c.lat, lng: c.lng };
    } catch {
      return this.lastPanCenter;
    }
  }

  /** Long-press on the map canvas to pick a destination pin. */
  onLongPress(listener: ((p: LatLng) => void) | null): void {
    this.longPressListener = listener;
  }

  /** Call after the map container size changes (e.g. topbar hide/show). */
  resize(): void {
    this.runWhenReady(() => {
      this.map.resize();
    });
  }

  /** EMA-smoothed heading — drives map bearing in 2d/tilted; north keeps
   *  bearing=0. Arrow tip uses viewport rotation (heading − mapBearing)
   *  so it stays screen-up in heading-up modes and geographic in north. */
  setTiltingHeading(deg: number): void {
    if (!Number.isFinite(deg)) return;
    this.tiltingHeadingDeg = wrap360(deg);
    this.runWhenReady(() => {
      this.syncUserMarkerRotation();
      this.syncFixedNavCursor();
      if (this.viewMode === 'north') return;
      if (this.liveNavigation && this.follow) return;
      if (this.follow && this.pendingUserPos) {
        this.easeToHeading(this.pendingUserPos, this.tiltingHeadingDeg, true);
      }
    });
  }

  /** Camera fit when the app boots with a saved-location. Idempotent:
   *  callers can fire-and-forget on every `loadSavedLocation()`; if the
   *  style isn't yet loaded, we queue the jump in `pendingOps`. */
  jumpToInitialView(p: LatLng, zoom: number): void {
    this.pendingUserPos = p;
    this.lastPanCenter = p;
    this.runWhenReady(() => {
      this.map.jumpTo({ center: [p.lng, p.lat], zoom });
    });
  }

  /** Recenter / auto-zoom — re-engage follow and put the user on screen. */
  recenterOnUser(override?: LatLng): void {
    if (override) {
      this.pendingUserPos = override;
      this.lastPanCenter = override;
    }
    this.setFollow(true);
    this.runWhenReady(() => {
      const target = this.pendingUserPos ?? this.lastPanCenter;
      if (!target) {
        const pose = this.cameraPose();
        this.map.easeTo({
          center: [config.defaultCenter.lng, config.defaultCenter.lat],
          zoom: config.defaultZoom,
          bearing: pose.bearing,
          pitch: pose.pitch,
          padding: pose.padding,
          duration: this.animMs(VIEW_TOGGLE_MS),
          essential: true,
        });
        return;
      }
      this.applyUserMarker(target, this.pendingUserPosAccuracy ?? undefined);
      if (this.liveNavigation) {
        this.frameNavigationView(target, this.navLookAt, this.animMs(VIEW_TOGGLE_MS));
        return;
      }
      this.frameUserOnScreen(target, this.animMs(VIEW_TOGGLE_MS));
    });
  }

  /** Whether the camera is still locked onto the user (true until the
   *  user drags the map; re-enabled by recenterOnUser / followNavigation). */
  isFollowing(): boolean {
    return this.follow;
  }

  /**
   * Live navigation camera: frame user + next turn while auto-follow is on.
   * Always eases toward the next pose so GPS ticks blend instead of jumping.
   */
  followNavigation(user: LatLng, lookAt: LatLng | null): void {
    if (!this.follow) return;
    this.pendingUserPos = user;
    this.navLookAt = lookAt;
    this.lastPanCenter = user;

    const bearing = this.tiltingHeadingDeg;
    const movedM = this.lastFollowCamUser
      ? haversineMeters(this.lastFollowCamUser, user)
      : Infinity;
    const bearingDelta = Number.isFinite(this.lastFollowCamBearing)
      ? bearingDeltaDeg(this.lastFollowCamBearing, bearing)
      : Infinity;
    const lookChanged = !sameLatLng(this.lastFollowCamLookAt, lookAt);
    const skipCamera =
      movedM < NAV_CAM_SKIP_M &&
      bearingDelta < NAV_CAM_SKIP_BEARING_DEG &&
      !lookChanged &&
      this.lastFollowCamUser !== null;

    this.runWhenReady(() => {
      this.applyUserMarker(user, this.pendingUserPosAccuracy ?? undefined);
      this.syncFixedNavCursor();
      if (skipCamera) return;
      this.lastFollowCamUser = user;
      this.lastFollowCamLookAt = lookAt;
      this.lastFollowCamBearing = bearing;
      this.frameNavigationView(
        user,
        lookAt,
        this.followEaseMs(movedM, bearingDelta),
      );
    });
  }

  /** Duration so consecutive GPS eases overlap instead of stop–start stutter. */
  private followEaseMs(movedM: number, bearingDelta: number): number {
    if (this.powerSaver) return this.animMs(NAV_FOLLOW_MS);
    if (!Number.isFinite(movedM) || movedM === Infinity) {
      return NAV_FOLLOW_MS;
    }
    const byDist = 550 + movedM * 40;
    const byTurn = bearingDelta * 22;
    return Math.min(
      NAV_FOLLOW_MS_MAX,
      Math.max(NAV_FOLLOW_MS_MIN, byDist + byTurn),
    );
  }

  /** Auto zoom/pan so the next manoeuvre stays on screen during nav. */
  private frameNavigationView(
    user: LatLng,
    lookAt: LatLng | null,
    durationMs: number,
  ): void {
    const dist = lookAt ? haversineMeters(user, lookAt) : 400;
    const center: [number, number] = [user.lng, user.lat];
    const easeOut = (t: number): number => 1 - (1 - t) * (1 - t);

    if (this.liveNavigation) {
      const pose = this.cameraPose();
      this.map.easeTo({
        center,
        bearing: pose.bearing,
        pitch: pose.pitch,
        zoom: zoomForLookahead(dist),
        padding: pose.padding,
        duration: durationMs,
        easing: easeOut,
        essential: true,
      });
      this.syncFixedNavCursor();
      return;
    }

    if (this.viewMode === 'tilted' || this.viewMode === '2d') {
      const pose = this.cameraPose();
      this.map.easeTo({
        center,
        bearing: pose.bearing,
        pitch: pose.pitch,
        zoom: zoomForLookahead(dist),
        padding: pose.padding,
        duration: durationMs,
        easing: easeOut,
        essential: true,
      });
      this.syncFixedNavCursor();
      return;
    }

    this.map.stop();
    const padding = this.northNavPadding();

    if (lookAt && dist > 30) {
      const bounds =
        dist < 70
          ? minSpanBounds(user, lookAt, MIN_BOUNDS_SPAN_DEG)
          : lngLatBounds([user, lookAt]);
      this.map.fitBounds(bounds, {
        padding,
        bearing: 0,
        pitch: 0,
        maxZoom: Math.min(19, zoomForLookahead(dist) + 0.5),
        minZoom: 12,
        duration: durationMs,
        essential: true,
      });
      return;
    }

    this.map.easeTo({
      center,
      bearing: 0,
      pitch: 0,
      zoom: zoomForLookahead(dist),
      padding,
      duration: durationMs,
      easing: easeOut,
      essential: true,
    });
  }

  private cameraPose(): {
    bearing: number;
    pitch: number;
    padding: { top: number; bottom: number; left: number; right: number } | undefined;
  } {
    if (this.viewMode === 'north') {
      return {
        bearing: 0,
        pitch: 0,
        padding: this.liveNavigation ? this.northCenterPadding() : undefined,
      };
    }
    const padding = this.headingNavPadding();
    if (this.viewMode === 'tilted') {
      return {
        bearing: this.tiltingHeadingDeg,
        pitch: TILTED_PITCH_DEG,
        padding,
      };
    }
    return {
      bearing: this.tiltingHeadingDeg,
      pitch: 0,
      padding,
    };
  }

  // -----------------------------------------------------------------------
  // View mode switch (North-up ⇆ Heading-up).
  // -----------------------------------------------------------------------
  /**
   * Switch between flat / tilted camera. Performs ONE easeTo with the
   * full (center, bearing, pitch) target so the transition is a single
   * smooth glide instead of two stitched calls.
   */
  setViewMode(mode: ViewMode): void {
    this.viewMode = mode;
    this.runWhenReady(() => {
      document.body.dataset.mapFollow = this.follow ? '1' : '0';
      if (this.pendingUserPos) {
        this.applyUserMarker(
          this.pendingUserPos,
          this.pendingUserPosAccuracy ?? undefined,
        );
      }
      this.syncFixedNavCursor();
      if (this.liveNavigation && this.follow && this.pendingUserPos) {
        this.frameNavigationView(
          this.pendingUserPos,
          this.navLookAt,
          this.animMs(VIEW_TOGGLE_MS),
        );
        return;
      }
      if (this.pendingUserPos) {
        this.frameUserOnScreen(this.pendingUserPos, this.animMs(VIEW_TOGGLE_MS));
        return;
      }
      const pose = this.cameraPose();
      this.map.easeTo({
        center: this.map.getCenter(),
        bearing: pose.bearing,
        pitch: pose.pitch,
        padding: pose.padding,
        duration: this.animMs(VIEW_TOGGLE_MS),
        essential: true,
      });
    });
  }

  /** Idle / non-nav framing — same camera move as a successful view-mode toggle. */
  private frameUserOnScreen(user: LatLng, durationMs: number): void {
    const pose = this.cameraPose();
    this.map.easeTo({
      center: [user.lng, user.lat],
      bearing: pose.bearing,
      pitch: pose.pitch,
      padding: pose.padding,
      duration: durationMs,
      essential: true,
    });
  }

  // -----------------------------------------------------------------------
  // Destination / stop markers.
  // -----------------------------------------------------------------------
  setDestination(p: LatLng, label: string, opts: { center?: boolean } = {}): void {
    this.setStops([{ location: p, label }], opts);
  }

  setStops(
    stops: Array<{ location: LatLng; label: string }>,
    opts: { center?: boolean } = {},
  ): void {
    const center = opts.center === true;
    this.runWhenReady(() => {
      this.clearStopMarkers();
      stops.forEach((stop, index) => {
        const el = document.createElement('div');
        el.className = 'dest-pin-wrap';
        const badge =
          stops.length > 1
            ? `<span class="dest-pin-num">${index + 1}</span>`
            : '';
        el.innerHTML = `<div class="dest-pin">${badge}\uD83D\uDCCD</div>`;
        el.title = stop.label;
        const marker = new maplibregl.Marker({ element: el, anchor: 'bottom' })
          .setLngLat([stop.location.lng, stop.location.lat])
          .setPopup(
            new maplibregl.Popup({ offset: 22, closeButton: false }).setText(stop.label),
          )
          .addTo(this.map);
        this.stopMarkers.push(marker);
      });

      const last = stops[stops.length - 1];
      if (!center || !last) return;
      this.setFollow(false);
      this.map.stop();
      const focus = () => {
        this.map.easeTo({
          center: [last.location.lng, last.location.lat],
          zoom: 14,
          bearing: 0,
          pitch: this.viewMode === 'tilted' ? TILTED_PITCH_DEG : 0,
          duration: this.powerSaver ? 0 : 500,
          padding: this.previewFocusPadding(),
        });
      };
      requestAnimationFrame(() => requestAnimationFrame(focus));
    });
  }

  private clearStopMarkers(): void {
    for (const marker of this.stopMarkers) marker.remove();
    this.stopMarkers = [];
  }

  /**
   * Padding so a preview destination sits in the free map band: below the
   * top chrome, above the route-details panel (not under it).
   */
  private previewFocusPadding(): {
    top: number;
    bottom: number;
    left: number;
    right: number;
  } {
    if (document.body.dataset.landscape === '1') {
      return { top: 24, bottom: 24, left: 28, right: 28 };
    }

    const vh = window.innerHeight || 700;
    let top = 88;
    let bottom = Math.round(vh * 0.45);

    const topbar = document.querySelector('.topbar');
    if (topbar instanceof HTMLElement) {
      top = Math.max(72, Math.round(topbar.getBoundingClientRect().bottom + 20));
    }

    const info = document.getElementById('info');
    if (
      info instanceof HTMLElement &&
      !info.hidden &&
      !info.classList.contains('panel-collapsed')
    ) {
      const covered = Math.max(0, vh - info.getBoundingClientRect().top);
      bottom = Math.max(bottom, Math.round(covered + 32));
    }

    bottom = Math.min(bottom, Math.floor(vh * 0.62));
    return { top, bottom, left: 28, right: 28 };
  }

  /** North-up nav framing: keep the next turn in the band between the
   *  maneuver banner and the bottom HUD. */
  private northNavPadding(): {
    top: number;
    bottom: number;
    left: number;
    right: number;
  } {
    if (document.body.dataset.landscape === '1') {
      let top = 24;
      const maneuver = document.getElementById('hud-next');
      if (maneuver instanceof HTMLElement && !maneuver.hidden) {
        top = Math.max(top, Math.round(maneuver.getBoundingClientRect().bottom + 12));
      }
      return { top, bottom: 28, left: 36, right: 28 };
    }

    const vh = window.innerHeight || 700;
    let top = 88;
    let bottom = Math.round(vh * 0.38);

    const maneuver = document.getElementById('hud-next');
    if (maneuver instanceof HTMLElement && !maneuver.hidden) {
      top = Math.max(top, Math.round(maneuver.getBoundingClientRect().bottom + 16));
    } else {
      const topbar = document.querySelector('.topbar');
      if (topbar instanceof HTMLElement && !topbar.hidden) {
        top = Math.max(72, Math.round(topbar.getBoundingClientRect().bottom + 20));
      }
    }

    const hud = document.getElementById('hud');
    if (hud instanceof HTMLElement && !hud.hidden) {
      const covered = Math.max(0, vh - hud.getBoundingClientRect().top);
      bottom = Math.max(bottom, Math.round(covered + 24));
    }
    bottom = Math.min(bottom, Math.floor(vh * 0.55));
    return { top, bottom, left: 28, right: 28 };
  }

  /**
   * Live north view: lock the user slightly above mid-band so the cursor
   * clears the map attribution strip at the bottom of the free map.
   */
  private northCenterPadding(): {
    top: number;
    bottom: number;
    left: number;
    right: number;
  } {
    const attributionClear = 40;
    if (document.body.dataset.landscape === '1') {
      return { top: 28, bottom: 28 + attributionClear, left: 28, right: 28 };
    }

    const vh = window.innerHeight || 700;
    let top = 72;
    let bottom = 72 + attributionClear;

    const maneuver = document.getElementById('hud-next');
    if (maneuver instanceof HTMLElement && !maneuver.hidden) {
      top = Math.max(top, Math.round(maneuver.getBoundingClientRect().bottom + 12));
    } else {
      const topbar = document.querySelector('.topbar');
      if (topbar instanceof HTMLElement && !topbar.hidden) {
        top = Math.max(56, Math.round(topbar.getBoundingClientRect().bottom + 12));
      }
    }

    const hud = document.getElementById('hud');
    if (hud instanceof HTMLElement && !hud.hidden) {
      const covered = Math.max(0, vh - hud.getBoundingClientRect().top);
      bottom = Math.max(bottom, Math.round(covered + 12 + attributionClear));
    }

    const maxInset = Math.floor((vh - 120) / 2);
    top = Math.min(top, maxInset);
    bottom = Math.min(bottom, Math.max(maxInset, attributionClear + 48));
    return { top, bottom, left: 28, right: 28 };
  }

  /**
   * Heading-up nav: keep road ahead dominant, but sit the cursor high
   * enough to clear the OSM attribution watermark.
   * MapLibre places `center` in the middle of the unpadded region —
   * larger top padding than bottom shifts that center downward.
   */
  private headingNavPadding(): {
    top: number;
    bottom: number;
    left: number;
    right: number;
  } {
    const attributionClear = 40;
    if (document.body.dataset.landscape === '1') {
      const vh = window.innerHeight || 400;
      return {
        top: Math.round(vh * 0.58),
        bottom: Math.max(attributionClear + 12, Math.round(vh * 0.2)),
        left: 20,
        right: 20,
      };
    }

    const vh = window.innerHeight || 700;
    let bottom = Math.round(vh * 0.14) + attributionClear;
    const hud = document.getElementById('hud');
    if (hud instanceof HTMLElement && !hud.hidden) {
      const covered = Math.max(0, vh - hud.getBoundingClientRect().top);
      bottom = Math.max(bottom, Math.round(covered + 8 + attributionClear));
    }
    bottom = Math.min(bottom, Math.floor(vh * 0.4));

    const band = 110;
    let top = Math.max(80, vh - bottom - band);
    const maneuver = document.getElementById('hud-next');
    if (maneuver instanceof HTMLElement && !maneuver.hidden) {
      top = Math.max(top, Math.round(maneuver.getBoundingClientRect().bottom + 8));
    }
    const maxTop = Math.max(80, vh - bottom - 72);
    top = Math.min(top, maxTop);
    return { top, bottom, left: 28, right: 28 };
  }

  // -----------------------------------------------------------------------
  // Route polyline + optional overview camera fit.
  // -----------------------------------------------------------------------
  /**
   * @param opts.fit — when true (default), fit the whole route in view
   *   and pause auto-follow. Pass `fit: false` during live navigation /
   *   auto-reroute so the polyline can update without yanking the camera
   *   back to a trip overview.
   * @param opts.keepFollow — when true, leave `follow` as-is (typically
   *   used with `fit: false` so GPS tracking stays engaged).
   */
  drawRoute(
    coords: LatLng[],
    opts: { fit?: boolean; keepFollow?: boolean } = {},
  ): void {
    if (coords.length < 2) return;
    const fit = opts.fit !== false;
    const keepFollow = opts.keepFollow === true;
    this.routeCoords = coords;
    const display = thinLatLng(coords, MAP_ROUTE_MAX_POINTS);
    this.runWhenReady(() => {
      const src = this.map.getSource('route') as maplibregl.GeoJSONSource | undefined;
      const done = this.map.getSource('route-done') as maplibregl.GeoJSONSource | undefined;
      if (!src) return;
      src.setData({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: display.map((c) => [c.lng, c.lat]),
        },
      });
      done?.setData(emptyLineString());
      if (!fit) {
        if (!keepFollow) {
          /* line-only update — don't touch follow */
        }
        return;
      }
      if (!keepFollow) this.setFollow(false);
      this.map.stop();
      this.fitFullRouteCamera(600);
    });
  }

  /**
   * During live nav: bright remaining polyline + dim traveled trail.
   * Call every GPS tick with the full route and progress meters.
   */
  setRouteProgress(coords: LatLng[], traveledMeters: number): void {
    if (coords.length < 2) return;
    this.routeCoords = coords;
    this.runWhenReady(() => {
      const index = this.routeProgressIndex;
      const { traveled, remaining } =
        index && index.route === coords
          ? index.splitAt(traveledMeters)
          : splitRouteAt(coords, traveledMeters);
      const remainingSrc = this.map.getSource('route') as maplibregl.GeoJSONSource | undefined;
      const doneSrc = this.map.getSource('route-done') as maplibregl.GeoJSONSource | undefined;
      if (!remainingSrc) return;
      const remainDraw = thinLatLng(
        remaining.length >= 2 ? remaining : coords.slice(-2),
        MAP_ROUTE_MAX_POINTS,
      );
      remainingSrc.setData(lineFeature(remainDraw));
      doneSrc?.setData(
        traveled.length >= 2
          ? lineFeature(thinLatLng(traveled, MAP_ROUTE_MAX_POINTS))
          : emptyLineString(),
      );
    });
  }

  /** Optional RouteIndex for faster progress splits during live nav. */
  setRouteProgressIndex(index: RouteIndex | null): void {
    this.routeProgressIndex = index;
  }

  /**
   * Frame the entire active route (flat, north-up).
   * Clears nav camera padding first — heading/tilted modes leave a large
   * asymmetric padding on the map that otherwise makes overview a no-op.
   */
  showRouteOverview(coords?: LatLng[]): boolean {
    if (coords && coords.length >= 2) {
      this.routeCoords = coords;
      if (this.routeCoords.length < 2) return false;
      this.setFollow(false);
      this.runWhenReady(() => {
        this.map.stop();
        this.map.setPadding({ top: 0, bottom: 0, left: 0, right: 0 });
        this.fitFullRouteCamera(700);
      });
      return true;
    }

    if (!this.pendingUserPos) return false;

    const lookAt = this.navLookAt;
    if (!lookAt) return false;

    const dist = haversineMeters(this.pendingUserPos, lookAt);

    const currentLookAt: LatLng = lookAt;
    const currentDist = dist;
    const currentUser: LatLng = this.pendingUserPos;

    this.setFollow(false);
    this.runWhenReady(() => {
      this.map.stop();
      this.map.setPadding({ top: 0, bottom: 0, left: 0, right: 0 });
      const bounds =
        currentDist < 70
          ? minSpanBounds(currentUser, currentLookAt, MIN_BOUNDS_SPAN_DEG)
          : lngLatBounds([currentUser, currentLookAt]);
      this.map.fitBounds(bounds, {
        padding: this.northNavPadding(),
        bearing: 0,
        pitch: 0,
        maxZoom: Math.min(19, zoomForLookahead(dist) + 0.5),
        minZoom: 12,
        duration: 700,
      });
    });
    return true;
  }

  private fitFullRouteCamera(durationMs: number): void {
    if (this.routeCoords.length < 2) return;
    let bounds = lngLatBounds(this.routeCoords);
    for (const alt of this.altRouteCoords) {
      if (alt.length < 2) continue;
      const ab = lngLatBounds(alt);
      bounds = extendBounds(bounds, ab[0]);
      bounds = extendBounds(bounds, ab[1]);
    }
    if (this.pendingUserPos) {
      bounds = extendBounds(bounds, [this.pendingUserPos.lng, this.pendingUserPos.lat]);
    }
    const padding = this.routeOverviewPadding();
    this.map.stop();
    this.map.fitBounds(bounds, {
      padding,
      bearing: 0,
      pitch: 0,
      maxZoom: 15,
      duration: this.animMs(durationMs),
      essential: true,
    });
  }

  /** Faint alternative routes under the primary polyline (planned overview). */
  setAlternativeRoutes(routes: LatLng[][]): void {
    this.altRouteCoords = routes.filter((r) => r.length >= 2);
    this.runWhenReady(() => {
      const src = this.map.getSource('route-alts') as maplibregl.GeoJSONSource | undefined;
      if (!src) return;
      if (this.altRouteCoords.length === 0) {
        src.setData(emptyLineCollection());
        return;
      }
      const features: LineFeature[] = this.altRouteCoords.map((coords, altIndex) => {
        const display = thinLatLng(coords, MAP_ROUTE_MAX_POINTS);
        return {
          type: 'Feature',
          properties: { altIndex },
          geometry: {
            type: 'LineString',
            coordinates: display.map((c) => [c.lng, c.lat] as [number, number]),
          },
        };
      });
      const collection: LineFeatureCollection = {
        type: 'FeatureCollection',
        features,
      };
      src.setData(collection);
    });
  }

  clearAlternativeRoutes(): void {
    this.altRouteCoords = [];
    this.runWhenReady(() => {
      const src = this.map.getSource('route-alts') as maplibregl.GeoJSONSource | undefined;
      src?.setData(emptyLineCollection());
    });
  }

  onAlternativeSelect(listener: ((index: number) => void) | null): void {
    this.altSelectListener = listener;
  }

  /** Chrome-aware padding so the full route sits in the free map band. */
  private routeOverviewPadding(): {
    top: number;
    bottom: number;
    left: number;
    right: number;
  } {
    const landscape = document.body.dataset.landscape === '1';
    const vh = window.innerHeight || (landscape ? 400 : 700);
    let top = landscape ? 36 : 88;
    let bottom = landscape ? 40 : 140;
    let left = 36;
    let right = 36;

    if (!landscape) {
      const topbar = document.querySelector('.topbar');
      if (topbar instanceof HTMLElement && !topbar.hidden) {
        top = Math.max(top, Math.round(topbar.getBoundingClientRect().bottom + 12));
      }
      const maneuver = document.getElementById('hud-next');
      if (maneuver instanceof HTMLElement && !maneuver.hidden) {
        top = Math.max(top, Math.round(maneuver.getBoundingClientRect().bottom + 12));
      }
      const hud = document.getElementById('hud');
      if (hud instanceof HTMLElement && !hud.hidden) {
        const covered = Math.max(0, vh - hud.getBoundingClientRect().top);
        bottom = Math.max(bottom, Math.round(covered + 16));
      }
      const info = document.getElementById('info');
      if (
        info instanceof HTMLElement &&
        !info.hidden &&
        !info.classList.contains('panel-collapsed')
      ) {
        const covered = Math.max(0, vh - info.getBoundingClientRect().top);
        bottom = Math.max(bottom, Math.round(covered + 16));
      }
      bottom = Math.min(bottom, Math.floor(vh * 0.55));
    }

    return { top, bottom, left, right };
  }

  clearRoute(): void {
    this.routeCoords = [];
    this.navLookAt = null;
    this.clearAlternativeRoutes();
    this.runWhenReady(() => {
      const src = this.map.getSource('route') as maplibregl.GeoJSONSource | undefined;
      const done = this.map.getSource('route-done') as maplibregl.GeoJSONSource | undefined;
      src?.setData(emptyLineString());
      done?.setData(emptyLineString());
    });
    this.clearStopMarkers();
    this.clearCameras();
    this.clearRoadworks();
    this.clearAmenities();
  }

  // -----------------------------------------------------------------------
  // POI layers (cameras + roadworks) — both backed by GeoJSON circle
  // layers for performance + GPU rendering.
  // -----------------------------------------------------------------------
  setCameras(
    radars: Array<{ lat: number; lng: number }>,
  ): void {
    this.runWhenReady(() => {
      const src = this.map.getSource('cameras') as maplibregl.GeoJSONSource | undefined;
      if (!src) return;
      src.setData({
        type: 'FeatureCollection',
        features: radars.map((r) => ({
          type: 'Feature',
          properties: {},
          geometry: { type: 'Point', coordinates: [r.lng, r.lat] },
        })),
      });
    });
  }

  clearCameras(): void {
    this.runWhenReady(() => {
      const src = this.map.getSource('cameras') as maplibregl.GeoJSONSource | undefined;
      src?.setData(emptyFeatureCollection());
    });
  }

  setRoadworks(roadworks: Roadwork[]): void {
    this.runWhenReady(() => {
      const src = this.map.getSource('roadworks') as maplibregl.GeoJSONSource | undefined;
      if (!src) return;
      src.setData({
        type: 'FeatureCollection',
        features: roadworks.map((r) => ({
          type: 'Feature',
          properties: { kind: r.kind },
          geometry: { type: 'Point', coordinates: [r.lng, r.lat] },
        })),
      });
    });
  }

  clearRoadworks(): void {
    this.runWhenReady(() => {
      const src = this.map.getSource('roadworks') as maplibregl.GeoJSONSource | undefined;
      src?.setData(emptyFeatureCollection());
    });
  }

  setAmenities(
    amenities: Array<{ lat: number; lng: number; kind: AmenityKind }>,
  ): void {
    this.runWhenReady(() => {
      const src = this.map.getSource('amenities') as maplibregl.GeoJSONSource | undefined;
      if (!src) return;
      src.setData({
        type: 'FeatureCollection',
        features: amenities.map((a) => ({
          type: 'Feature',
          properties: { kind: a.kind },
          geometry: { type: 'Point', coordinates: [a.lng, a.lat] },
        })),
      });
    });
  }

  clearAmenities(): void {
    this.runWhenReady(() => {
      const src = this.map.getSource('amenities') as maplibregl.GeoJSONSource | undefined;
      src?.setData(emptyFeatureCollection());
    });
  }

  setPowerSaver(on: boolean): void {
    this.powerSaver = on;
  }

  private animMs(normal: number): number {
    return this.powerSaver ? Math.min(100, Math.round(normal * 0.2)) : normal;
  }

  // -----------------------------------------------------------------------
  // Internal helpers (not part of the public surface).
  // -----------------------------------------------------------------------
  /**
   * Geographic "you are here" marker. North: arrow while moving, circle
   * when stationary. 2d/tilted: always heading arrow. Lat/lng-anchored.
   * Arrow uses viewport alignment so tip = travel direction on screen
   * (straight up once camera bearing matches heading).
   */
  private applyUserMarker(p: LatLng, accuracy?: number): void {
    const kind: 'dot' | 'arrow' =
      this.viewMode === 'north' && !this.userMoving ? 'dot' : 'arrow';
    if (!this.userMarker || this.userMarkerKind !== kind) {
      this.dropUserMarker();
      const el = document.createElement('div');
      if (kind === 'arrow') {
        el.className = 'user-marker user-marker-arrow';
        el.innerHTML =
          '<svg viewBox="0 0 32 40" width="32" height="40" aria-hidden="true"><path d="M16 0 L32 38 L16 28 L0 38 Z" fill="#38bdf8" stroke="#0f172a" stroke-width="1.5" stroke-linejoin="round"/></svg>';
        this.userMarker = new maplibregl.Marker({
          element: el,
          anchor: 'center',
          rotationAlignment: 'viewport',
          pitchAlignment: 'viewport',
        })
          .setLngLat([p.lng, p.lat])
          .addTo(this.map);
        this.syncUserMarkerRotation();
        this.syncFixedNavCursor();
      } else {
        el.className = 'user-marker';
        el.innerHTML =
          '<div class="user-marker-dot"></div><div class="user-marker-pulse"></div>';
        this.userMarker = new maplibregl.Marker({ element: el, anchor: 'center' })
          .setLngLat([p.lng, p.lat])
          .addTo(this.map);
      }
      this.userMarkerKind = kind;
    } else {
      this.userMarker.setLngLat([p.lng, p.lat]);
      if (kind === 'arrow') {
        this.syncUserMarkerRotation();
      }
    }
    if (kind === 'dot') {
      const dot = this.userMarker.getElement().querySelector<HTMLElement>('.user-marker-dot');
      if (dot) {
        const scale =
          accuracy && accuracy > 0 ? Math.min(1.6, 1 + accuracy / 100) : 1;
        dot.style.transform = `scale(${scale.toFixed(2)})`;
      }
    }
  }

  /** Keep arrow tip = travel direction relative to the screen. */
  private syncUserMarkerRotation(): void {
    if (!this.userMarker || this.userMarkerKind !== 'arrow') return;
    if (this.usesFixedNavCursor()) return;
    const mapBearing = this.map.getBearing();
    this.userMarker.setRotation(wrap360(this.tiltingHeadingDeg - mapBearing));
  }

  /** Live follow: pin a screen-fixed cursor; camera eases under it. */
  private usesFixedNavCursor(): boolean {
    return this.liveNavigation && this.follow;
  }

  private syncFixedNavCursor(): void {
    const fixed = this.usesFixedNavCursor();
    document.body.dataset.navCursor = fixed ? 'fixed' : 'geo';
    const overlay = document.getElementById('user-arrow');
    if (overlay instanceof HTMLElement) {
      overlay.hidden = !fixed;
      overlay.setAttribute('aria-hidden', fixed ? 'false' : 'true');
      if (fixed) {
        const pad = this.cameraPose().padding;
        const rect = this.container.getBoundingClientRect();
        if (pad && rect.width > 0 && rect.height > 0) {
          const cx =
            rect.left + pad.left + (rect.width - pad.left - pad.right) / 2;
          const cy =
            rect.top + pad.top + (rect.height - pad.top - pad.bottom) / 2;
          overlay.style.left = `${Math.round(cx)}px`;
          overlay.style.top = `${Math.round(cy)}px`;
          overlay.style.bottom = 'auto';
          overlay.style.right = 'auto';
        }
        if (this.viewMode === 'north') {
          overlay.style.transform = `translate(-50%, -50%) rotate(${this.tiltingHeadingDeg.toFixed(1)}deg)`;
        } else {
          overlay.style.transform = 'translate(-50%, -50%)';
        }
      } else {
        overlay.style.left = '';
        overlay.style.top = '';
        overlay.style.bottom = '';
        overlay.style.transform = '';
      }
    }
    if (this.userMarker) {
      this.userMarker.getElement().style.visibility = fixed ? 'hidden' : 'visible';
    }
  }

  private dropUserMarker(): void {
    if (this.userMarker) {
      this.userMarker.remove();
      this.userMarker = null;
    }
    this.userMarkerKind = null;
  }

  /**
   * Threshold-panTo: skip panning when the user hasn't moved >15 m.
   * This is the antidote to GPS jitter at red lights. Used in 2D mode.
   */
  private thresholdPanTo(p: LatLng): void {
    const displacement = this.lastPanCenter
      ? haversineMeters(this.lastPanCenter, p)
      : Infinity;
    if (!this.lastPanCenter || displacement > PAN_THRESHOLD_METERS) {
      this.map.easeTo({
        center: [p.lng, p.lat],
        bearing: 0,
        pitch: 0,
        duration: this.lastPanCenter ? 400 : 0,
      });
      this.lastPanCenter = p;
    }
  }

  /** Camera update when heading changes in 2D / tilted follow. */
  private easeToHeading(p: LatLng, bearingDeg: number, immediate: boolean): void {
    if (!this.styleReady) return;
    const pose = this.cameraPose();
    this.map.easeTo({
      center: [p.lng, p.lat],
      bearing: bearingDeg,
      pitch: pose.pitch,
      padding: pose.padding,
      duration: immediate ? 0 : this.animMs(TICK_EASE_MS),
    });
  }

  destroy(): void {
    this.cancelLongPress();
    const canvas = this.map.getCanvas();
    canvas.removeEventListener('pointerdown', this.onLongPressPointerDown);
    canvas.removeEventListener('pointermove', this.onLongPressPointerMove);
    canvas.removeEventListener('pointerup', this.onLongPressPointerUp);
    canvas.removeEventListener('pointercancel', this.onLongPressPointerUp);
    canvas.removeEventListener('contextmenu', this.onLongPressContextMenu);
    this.map.remove();
  }

  private handleLongPressPointerDown(e: PointerEvent): void {
    if (!this.longPressListener) return;
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    const target = e.target;
    if (!(target instanceof Element)) return;
    if (target.closest('.maplibregl-ctrl, .maplibregl-marker')) return;

    this.cancelLongPress();
    this.longPressStart = { x: e.clientX, y: e.clientY, pointerId: e.pointerId };
    this.container.classList.add('is-long-pressing');
    this.longPressTimer = window.setTimeout(() => {
      const start = this.longPressStart;
      if (!start || !this.longPressListener) return;
      const rect = this.map.getCanvas().getBoundingClientRect();
      const lngLat = this.map.unproject([start.x - rect.left, start.y - rect.top]);
      if (typeof navigator.vibrate === 'function') navigator.vibrate(12);
      this.longPressListener({ lat: lngLat.lat, lng: lngLat.lng });
      this.cancelLongPress();
    }, 550);
  }

  private handleLongPressPointerMove(e: PointerEvent): void {
    const start = this.longPressStart;
    if (!start || start.pointerId !== e.pointerId) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (dx * dx + dy * dy > 12 * 12) this.cancelLongPress();
  }

  private handleLongPressPointerUp(e: PointerEvent): void {
    if (this.longPressStart?.pointerId === e.pointerId) this.cancelLongPress();
  }

  private preventLongPressMenu(e: Event): void {
    if (!this.longPressListener) return;
    e.preventDefault();
  }

  private cancelLongPress(): void {
    if (this.longPressTimer !== null) window.clearTimeout(this.longPressTimer);
    this.longPressTimer = null;
    this.longPressStart = null;
    this.container.classList.remove('is-long-pressing');
  }
}

// -----------------------------------------------------------------------
// Free-standing helpers for GeoJSON boilerplate & bounds math.
// -----------------------------------------------------------------------
function emptyLineString(): LineFeature {
  return {
    type: 'Feature',
    properties: {},
    geometry: { type: 'LineString', coordinates: [] },
  };
}

function emptyLineCollection(): LineFeatureCollection {
  return { type: 'FeatureCollection', features: [] };
}

function lineFeature(coords: LatLng[]): LineFeature {
  return {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'LineString',
      coordinates: coords.map((c) => [c.lng, c.lat]),
    },
  };
}

function emptyFeatureCollection(): PointFeatureCollection {
  return { type: 'FeatureCollection', features: [] };
}

function wrap360(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

function bearingDeltaDeg(a: number, b: number): number {
  const d = Math.abs(wrap360(a) - wrap360(b));
  return d > 180 ? 360 - d : d;
}

function sameLatLng(a: LatLng | null, b: LatLng | null): boolean {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  return Math.abs(a.lat - b.lat) < 1e-6 && Math.abs(a.lng - b.lng) < 1e-6;
}

/** Zoom from distance to the next manoeuvre (m) — smooth curve, not steps. */
function zoomForLookahead(distanceM: number): number {
  const d = Math.max(50, Math.min(6_000, distanceM));
  const z = 20.4 - Math.log10(d) * 2.3;
  return Math.max(12.5, Math.min(18, z));
}

function minSpanBounds(
  a: LatLng,
  b: LatLng,
  minSpanDeg: number,
): [[number, number], [number, number]] {
  const midLat = (a.lat + b.lat) / 2;
  const midLng = (a.lng + b.lng) / 2;
  const half = minSpanDeg / 2;
  return [
    [midLng - half, midLat - half],
    [midLng + half, midLat + half],
  ];
}

/**
 * Compute a `[[sw_lng, sw_lat], [ne_lng, ne_lat]]` LngLatBounds from
 * a list of `LatLng`. Centralises the GeoJSON-shaped array-of-arrays
 * MapLibre expects for `fitBounds`. We DO NOT attach `.extend` to the
 * returned array (that runtime hack confused refactors) — use
 * `extendBounds` below to grow a bounds.
 */
function lngLatBounds(coords: LatLng[]): [[number, number], [number, number]] {
  let minLng = coords[0]!.lng;
  let maxLng = coords[0]!.lng;
  let minLat = coords[0]!.lat;
  let maxLat = coords[0]!.lat;
  for (const c of coords) {
    if (c.lng < minLng) minLng = c.lng;
    if (c.lng > maxLng) maxLng = c.lng;
    if (c.lat < minLat) minLat = c.lat;
    if (c.lat > maxLat) maxLat = c.lat;
  }
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
}

/** Grow an existing `[[sw],[ne]]` bounds to include one `[lng, lat]`. */
function extendBounds(
  bounds: [[number, number], [number, number]],
  point: [number, number],
): [[number, number], [number, number]] {
  const [lng, lat] = point;
  const [sw, ne] = bounds;
  return [
    [Math.min(sw[0], lng), Math.min(sw[1], lat)],
    [Math.max(ne[0], lng), Math.max(ne[1], lat)],
  ];
}
